import { useState, useRef, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getBlobFileContent, useWarmup } from '../utils/helpers';
import ProgressBar from '../Components/ProgressBar';
import './CustomerProcessPage.css'
import axios from 'axios';

const processingRoute = process.env.REACT_APP_PROCESSING;
const blobBase = process.env.REACT_APP_BLOB_SAS_BASE;
const blobToken = process.env.REACT_APP_BLOB_SAS_TOKEN;
const wakePing = process.env.REACT_APP_WAKE_PING;

const formatDate = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return dateValue; // Return original if not a valid date

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}-${day}-${year}`;
};



const CustomerProcessPage = () => {
  const initialProcessingData = {
    approvedBy: '',
    approvedDate: '',
    assignedSalesperson: '',
    creditLimit: '',
    customerCode: '',
    billTo: '',
    taxCode: '',
    territory: '',
    branch: '',
    terms: 'NET 30',
    cod: false,
    salesTax: false,
    rentTax: false,
    leaseTax: false,
    // Names sent to TIMS (max 50 chars each) — pre-filled from the application, editable by the processor
    timsCustomerName: '',
    timsShipToName: ''
  };

  const [processingData, setProcessingData] = useState(initialProcessingData);
  const navigate = useNavigate();

  const [isReviewing, setIsReviewing] = useState(false);
  const [toast, setToast] = useState({ type: '', message: '' });
  const toastTimeout = useRef(null);
  const [applicantId, setApplicantId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const isFormUnlocked = /^\d{5}$/.test(applicantId) && customerName;
  const [taxCodes, setTaxCodes] = useState([]);
  const [branchCodes, setBranchCodes] = useState([]);
  const [territoryCodes, setTerritoryCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckLoading, setIsCheckLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState(null);
  const [timsErrors, setTimsErrors] = useState([]); // TIMS validation reasons the customer was rejected
  const [successInfo, setSuccessInfo] = useState(null); // { name, code } once TIMS confirms the customer was created
  const [status, setStatus] = useState(null);
  const [hasCheckedCode, setHasCheckedCode] = useState(false);
  const [statusIndex, setStatusIndex] = useState(0);
  const [customerApplicationData, setCustomerApplicationData] = useState(null);


  const salesPersonOptions = process.env.REACT_APP_SALESPEOPLE_OPTIONS.split(',').map(opt => opt.trim());



  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username;

  const TerritorySelect = ({ value, onChange, disabled, territoryCodes }) => {
    const [isOpen, setIsOpen] = useState(false);

    const containerRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const longestLabel = useMemo(() => {
      if (!territoryCodes?.length) return 0;
      return territoryCodes.reduce((max, opt) =>
        opt.label.length > max ? opt.label.length : max, 0
      );
    }, [territoryCodes]);

    // rough width calc: assume ~6px per character + padding
    const approxWidth = `${longestLabel * 6}px`;

    return (
      <div
        className={` ${disabled ? 'disabled' : ''}`}
        style={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
        ref={containerRef}
      >
        <label style={{ fontWeight: "bold", marginBottom: "5px" }}>Territory</label>
        <div
          className="select-display"
          onClick={() => setIsOpen(prev => !prev)}
          style={{ minWidth: approxWidth }}
        >
          {
            territoryCodes.find((opt) => opt.value === value)?.label || "Select"
          }
          <span className="arrow">{isOpen ? '▲' : '▼'}</span>
        </div>

        <AnimatePresence>
          {!disabled && isOpen && (
            <motion.ul
              className="process-options-list"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {territoryCodes.map((option) => (
                <li key={option.value} className="select-option" onClick={() => { onChange(option.value); setIsOpen(false); }}>
                  {option.label}
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    );
  }

  const BranchSelect = ({ value, onChange, disabled, branchCodes }) => {
    const [isOpen, setIsOpen] = useState(false);

    const containerRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const longestLabel = useMemo(() => {
      if (!branchCodes?.length) return 0;
      return branchCodes.reduce((max, opt) =>
        opt.label.length > max ? opt.label.length : max, 0
      );
    }, [branchCodes]);

    // rough width calc: assume ~6px per character + padding
    const approxWidth = `${longestLabel * 6}px`;

    return (
      <div
        className={` ${disabled ? 'disabled' : ''}`}
        style={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
        ref={containerRef}>
        <label style={{ fontWeight: "bold", marginBottom: "5px" }}>Branch</label>
        <div
          className="select-display"
          onClick={() => setIsOpen(prev => !prev)}
          style={{ minWidth: approxWidth }}
        >
          {
            branchCodes.find((opt) => opt.value === value)?.label || "Select"
          }
          <span className="arrow">{isOpen ? '▲' : '▼'}</span>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.ul
              className="process-options-list"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {branchCodes.map((option) => (
                <li key={option.value} className="select-option" onClick={() => { onChange(option.value); setIsOpen(false); }}>
                  {option.label}
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    );
  }

  const SalespersonSelect = ({ value, onChange, disabled, salesPersonOptions = [] }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const longestLabel = useMemo(() => {
      if (!salesPersonOptions?.length) return 0;
      return salesPersonOptions.reduce((max, opt) =>
        opt.length > max ? opt.length : max, 0
      );
    }, [salesPersonOptions]);

    // rough width calc: assume ~8px per character + padding
    const approxWidth = `${longestLabel * 6}px`;

    return (
      <div
        className={`${disabled ? 'disabled' : ''}`}
        style={{
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto', // 🛑 disables all mouse events
          width: '200px'
        }}
        ref={containerRef}>
        <label style={{ fontWeight: "bold", marginBottom: "5px" }}>Assigned Salesperson</label>
        <div className="select-display" onClick={() => setIsOpen(prev => !prev)} style={{ width: approxWidth }}>
          {value || 'Select'}
          <span className="arrow">{isOpen ? '▲' : '▼'}</span>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.ul
              className="process-options-list"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ width: '200px' }}
            >
              {salesPersonOptions.map((option) => (
                <li key={option} className="select-option" onClick={() => { onChange(option); setIsOpen(false); }} >
                  {option}
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    );
  }

  const TaxCodeSelect = ({ value, onChange, disabled, taxCodes = [] }) => {
    const [query, setQuery] = useState(value || "");
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
      setQuery(value || "");
    }, [value]);

    const filteredCodes = useMemo(() => {
      if (query.length < 2) return [];
      return (taxCodes || []).filter((code) =>
        code.value.toLowerCase().includes(query.toLowerCase()) ||
        code.label.toLowerCase().includes(query.toLowerCase())
      );
    }, [query, taxCodes]);

    const containerRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
      <div
        className={`${disabled ? "disabled" : ""}`}
        style={{
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
        ref={containerRef}
      >
        <label style={{ fontWeight: "bold", marginBottom: "5px" }}>Tax Code</label>
        <input
          type="text"
          value={query}
          disabled={disabled}
          placeholder="Start Typing..."
          onChange={(e) => {
            const newValue = e.target.value;
            setQuery(newValue);
            if (newValue.length >= 2) setIsOpen(true);  // 👈 stays open for ≥2 chars
            else setIsOpen(false);
          }}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          style={{
            width: "100%",
            padding: "8px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            marginBottom: 0,
            fontWeight: "bold",
            textAlign: "center",
            fontSize: "16px",
          }}
        />

        <AnimatePresence>
          {!disabled && isOpen && (
            <motion.ul
              className="process-options-list"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ width: "300px" }}
            >
              {filteredCodes.length > 0 ? (
                filteredCodes.map((option) => (
                  <li
                    key={option.value}
                    className="select-option"
                    onClick={() => {
                      onChange(option.value);   // updates processingData.taxCode
                      setQuery(option.value);   // display selection
                      setIsOpen(false);         // close after selection
                    }}
                  >
                    <strong>{option.label}</strong>
                  </li>
                ))
              ) : (
                <li className="select-option disabled">No matches</li> // 👈 keep open but show "no matches"
              )}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    );
  };

  useWarmup(`${wakePing}${processingRoute}`); // Warm up every 10 minutes

  // locks form until applicantId is 5 digits and customerName is found
  useEffect(() => {
    if (!isFormUnlocked) {
      setProcessingData(initialProcessingData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormUnlocked]);

  useEffect(() => {
    if (toast.message) {
      // set timeout
      toastTimeout.current = setTimeout(() => {
        setToast({ type: "", message: "" });
      }, 5000);

      // attach click handler
      const handleClickAnywhere = () => {
        clearTimeout(toastTimeout.current);
        setToast({ type: "", message: "" });
      };

      document.addEventListener("click", handleClickAnywhere);

      // cleanup
      return () => {
        document.removeEventListener("click", handleClickAnywhere);
        clearTimeout(toastTimeout.current);
      };
    }
  }, [toast.message]);

  // useEffect grabs customer details from Azure Blob Storage when applicantId matches a user in the storage container
  useEffect(() => {
    const fetchName = async () => {
      try {
        const blobURL = `${blobBase}applicants/${applicantId}.json${blobToken}`;
        const content = await getBlobFileContent(blobURL);
        const customer = JSON.parse(content);
        setCustomerName(customer.CustomerName || 'Unknown');

        if (!processingData.salesperson) {
          const fullSalesperson = salesPersonOptions.find(opt =>
            opt.startsWith(customer.Salesperson)
          );

          setProcessingData(prev => ({
            ...prev,
            salesperson: fullSalesperson || ''
          }));
        }

        // Pre-fill the TIMS names the same way the backend derives them
        const info = customer.CustomerInfo || {};
        const personalName = [info.personal?.legalFirstName, info.personal?.lastName].filter(Boolean).join(' ');
        const customerNameForTims = info.commercial?.companyLegalName || info.personal?.lastName || '';
        setProcessingData(prev => ({
          ...prev,
          timsCustomerName: customerNameForTims.toUpperCase(),
          timsShipToName: (info.shippingInfo?.shippingBusinessName || info.commercial?.companyLegalName || personalName).toUpperCase()
        }));

        // Capture full customer application data for display in left panel
        setCustomerApplicationData(customer);
        console.log('📦 Customer application data loaded:', customer);
        console.log('📦 Data structure check:', {
          hasCustomerInfo: !!customer.CustomerInfo,
          hasCommercial: !!customer.CustomerInfo?.commercial,
          hasPersonal: !!customer.CustomerInfo?.personal,
          hasAddressInfo: !!customer.CustomerInfo?.addressInfo,
          hasShippingInfo: !!customer.CustomerInfo?.shippingInfo,
          hasTradeReferences: !!customer.CustomerInfo?.tradeReferences,
          allKeys: Object.keys(customer)
        });

      } catch (err) {
        console.error('Failed to fetch applicant blob:', err);
      }
    };

    if (applicantId.length === 5) {
      fetchName();
    } else {
      setCustomerName('');
      setCustomerApplicationData(null);
      setProcessingData(prev => ({ ...prev, salesperson: '', timsCustomerName: '', timsShipToName: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantId]);

  useEffect(() => {
    const fetchTerritoryAndBranchCodes = async () => {
      try {
        const res = await axios.post("/api/getcodes"); // 👈 POST request
        const data = res.data; // axios automatically parses JSON

        setBranchCodes(data.branchCodes);
        setTerritoryCodes(data.territoryCodes);
        setTaxCodes(data.taxCodes);
      } catch (err) {
        console.error("❌ Error fetching codes:", err.message);
      }
    };

    fetchTerritoryAndBranchCodes();
  }, []);

  const requiredValidation = {
    approvedBy: "Approved By",
    salesperson: "Salesperson",
    creditLimit: "Credit Limit",
    customerCode: "Customer Code",
    taxCode: "Tax Code",
    territory: "Territory",
    branch: "Branch",
    timsCustomerName: "TIMS Customer Name",
    timsShipToName: "TIMS Ship-To Name"
  }

  const TIMS_NAME_MAX = 50;

  const validateProcessingData = () => {
    const missing = [];

    for (const key in requiredValidation) {
      if (!processingData[key] || processingData[key].toString().trim() === "") {
        missing.push(requiredValidation[key]);
      }
    }

    ["timsCustomerName", "timsShipToName"].forEach(key => {
      if ((processingData[key] || "").length > TIMS_NAME_MAX) {
        missing.push(`${requiredValidation[key]} (max ${TIMS_NAME_MAX} characters)`);
      }
    });

    if (!hasCheckedCode) {
      missing.push("Customer Code (must be checked)");
    } else if (status === "❌ In Use") {
      missing.push("Customer Code (already in use)");
    }

    return missing;
  };


  const handleChange = (field) => (e) => {
    const value = e.target?.type === "checkbox" ? !!e.target.checked : e.target.value;
    setProcessingData(prev => ({ ...prev, [field]: value }));
  };

  const toggle = (field) => () => {
    setProcessingData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const missingFields = validateProcessingData();
    if (missingFields.length > 0) {
      const errorList = (
        <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
          {missingFields.map(field => (
            <li key={field}>{field}</li>
          ))}
        </ul>
      );

      setIsReviewing(false);

      setToast({
        type: "error",
        message: (
          <div
            className="custom-scroll"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              maxHeight: "400px",          // cap height
              overflowY: "auto",           // scroll if needed
              width: "100%",               // take full width of toast
            }}
          >
            <h2 style={{ margin: "0 0 0.5rem 0", fontWeight: "bold" }}>
              Missing Required Fields
            </h2>
            <p style={{ margin: "0 0 0.5rem 0", fontWeight: "lighter" }}>
              The following fields are required before review:
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",  // two columns
                gap: "0.5rem 2rem",              // row/col spacing
                width: "100%",
              }}
            >
              {errorList}
            </div>
          </div>
        ),
      });
      setTimeout(() => {
        setToast({ type: "", message: "" });
      }, 7000);

      return;

    } 

    if(!hasCheckedCode) {
      setToast({
        type: "error",
        message: "⚠️ Customer Code must be checked before proceeding."
      });
      return;
    } 

    if (status === "❌ In Use") {
      setToast({
        type: "error",
        message: "⚠️ Customer Code is already in use. Please try again."
      });
      return;
    }

      setIsReviewing(true);
  }

  const processingPhrases = useMemo(() => [
    "Packaging the request into JSON format…",
    "Converting your submission into machine‑friendly gibberish…",
    "Pushing data through the Azure hamster wheel…",
    "Sending data to TIMS for validation…",
    "Checking if Salesperson really exists…",
    "Validating credit limit against the cosmic database…",
    "Routing PDF to the printer in the cloud…",
    "Generating PDF copies for internal records…",
    "Checking for duplicate customer codes…",
    "Crossing fingers for tax code validation…",
    "Updating the CRM database with new details…",
    "Running tax code verifications…",
    "Routing through Azure Functions pipeline…",
    "Talking nicely to the database...",
    "Chasing down stray packets on the internet...",
    "Summoning digital approval spirits…",
    "Translating form fields into corporate speak…",
    "Making absolutely sure this customer isn’t a robot…",
    "Verifying data like your job depends on it (it doesn’t)…",
    "Performing one last reality check, can't let Dwight beat me this time…",
    "Finalizing everything, please hold tight..."
  ], []);

  // FAKE CREEP PROGRESS
useEffect(() => {
  let interval;
  if (isLoading) {
    setProgress(0);
    interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 99) return prev;
        const increment = Math.floor(Math.random() * 2) + 1;
        return Math.min(prev + increment, 95);
      });

      setStatusIndex(prev => {
        if (prev >= processingPhrases.length - 1) return prev;
        return prev + 1;
      });
    }, 12000);
  }

  return () => clearInterval(interval);
}, [isLoading, processingPhrases.length]);

function extractApiError(error) {
  // Timeout / network
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.';
  }
  if (error.message === 'Network Error') {
    return 'Network error — check your connection and try again.';
  }

  const res = error.response;
  if (!res) {
    // No response at all (CORS, DNS, offline, etc.)
    return error.message || 'Unknown client error';
  }

  const data = res.data;

  // If our function returned { ok:false, code, message, where }
  if (data && typeof data === 'object') {
    const msg = data.message || data.error || res.statusText || 'Server error';
    const where = data.where ? ` (${data.where})` : '';
    return `${msg}${where}`;
  }

  // If the function returned a string body
  if (typeof data === 'string') {
    return `${data} (${res.status} ${res.statusText})`;
  }

  // Fallback
  return `${res.status} ${res.statusText}`;
}


  const handleModalSubmit = async () => {
    setIsReviewing(false);
    setIsLoading(true);
    setProgress(0);

    const asTaxNumber = (b) => (b ? 63 : 0);

    const internalUse = {
      applicantId,
      approvedBy: processingData.approvedBy,
      approvedDate: new Date().toISOString(),
      salesperson: processingData.salesperson.slice(0, 6),
      creditLimit: processingData.creditLimit,
      cod: Boolean(processingData.cod),
      customerCode: processingData.customerCode,
      billTo: processingData.billTo || '',
      taxCode: processingData.taxCode,
      territory: processingData.territory,
      branch: processingData.branch,
      terms: "NET 30",
      salesTax: asTaxNumber(Boolean(processingData.salesTax)),
      rentTax: asTaxNumber(Boolean(processingData.rentTax)),
      leaseTax: asTaxNumber(Boolean(processingData.leaseTax)),
      processedBy: userEmail,
      timsCustomerName: processingData.timsCustomerName.trim().toUpperCase(),
      timsShipToName: processingData.timsShipToName.trim().toUpperCase(),
    };

    try {
      const response = await axios.post(
        processingRoute,
        internalUse,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 120000, // 2 minutes timeout
        }
      );

      if (response.data && response.data.ok === false) {
        const where = response.data.where ? ` (${response.data.where})` : '';
        const msg = `${response.data.message || 'Request failed'}${where}`;
        setToast({ type: 'error', message: msg });
        setLastError(msg);
        return; // stop success flow
      }

      setSuccessInfo({
        name: processingData.timsCustomerName.toUpperCase() || customerName,
        code: processingData.customerCode.toUpperCase(),
        internalEmailSent: response.data?.internalEmailSent !== false,
        customerEmailSent: response.data?.customerEmailSent !== false,
      });
      setProcessingData(initialProcessingData);
      setApplicantId('');
      setCustomerName('');
      setRetryCount(0);
      setIsReviewing(false);

    } catch (error) {
      console.error('❌ Error processing customer:', error);
      const data = error.response?.data;
      if (data?.code === 'TIMS_VALIDATION') {
        // TIMS refused the customer — show why; retrying the same data won't help
        setTimsErrors(data.validationMessages || []);
        setLastError(data.message || 'Customer was NOT created in TIMS.');
        return;
      }
      const message = extractApiError(error);
      setToast({ type: 'error', message: `Error processing customer: ${message}` });
      setLastError(message);
      setRetryCount(prev => prev + 1);
    } finally {
      setProgress(100);
      setTimeout(() => setIsLoading(false), 3000);
      setTimeout(() => setToast({ type: '', message: '' }), 5000);
    }

    setTimeout(() => {
      setToast({ type: '', message: '' });
    }, 5000);
  };

  const handleRetry = async () => {
    // should allow user to go back to the review modal screen where they can click the submit button again and try and resubmit because there might be a cold-start issue
    setLastError(false);
    setIsLoading(false);
    setIsReviewing(true);
    setRetryCount(prev => prev + 1);
    // if retry count reaches 3, reset to initial state
    if (retryCount === 3) {
      setRetryCount(0);
    }

  }

  const handleCodeCheck = async () => {
    if (!processingData.customerCode) return;
    setIsCheckLoading(true);
    setStatus(null);

    try {
      const response = await axios.post("/api/customercodecheck", {
        customerCode: processingData.customerCode.toUpperCase()
      });

      setHasCheckedCode(true);

      if (response.data.exists) {
        setStatus("❌ In Use");
      } else {
        setStatus("✅ Available");
      }
    } catch (error) {
      console.error("Error checking code:", error);
      setStatus("⚠️ Error checking code.");
      setHasCheckedCode(false);
    } finally {
      setIsCheckLoading(false);
    }
  };



  const checkboxDisabledStyle = { opacity: !isFormUnlocked ? 0.6 : 1, pointerEvents: !isFormUnlocked ? 'none' : 'auto', fontWeight: 'bold', fontSize: '16px', padding: '10px' };
  const inputLabelDisabledStyle = { opacity: !isFormUnlocked ? 0.6 : 1, pointerEvents: !isFormUnlocked ? 'none' : 'auto', display: 'flex', flexDirection: 'column', flexWrap: 'wrap', fontWeight: 'bold', fontSize: '16px', marginBottom: '5px' };
  const buttonDisabledStyle = { opacity: !isFormUnlocked ? 0.6 : 1, pointerEvents: !isFormUnlocked ? 'none' : 'auto', fontSize: '16px' };
  const fieldsetDisabledStyle = {
    opacity: !isFormUnlocked ? 0.6 : 1,
    pointerEvents: !isFormUnlocked ? 'none' : 'auto',
    border: "2px solid #2A3990",
    borderRadius: "8px",
  };


 
  return (
    <div>
      <section className='title-section'>
        <h1 >Process Customer</h1>
        <p>Please Note: <strong>Applicant ID</strong> is required.</p>
      </section>

      <div style={{ display: 'flex', gap: '20px', maxWidth: '95vw', margin: '0 auto', padding: '0 20px' }}>
        {/* Left Panel - Customer Application Data */}
        {customerApplicationData && (
          <div
            className="customer-info-panel"
            style={{
              flex: '0 0 400px',
              maxHeight: 'calc(100vh - 200px)',
              overflowY: 'auto',
              backgroundColor: '#f8f9fa',
              border: '2px solid #2A3990',
              borderRadius: '8px',
              padding: '1.5rem',
              boxSizing: 'border-box'
            }}
          >
            <h3 style={{ color: '#2A3990', textAlign: 'center', marginTop: 0, marginBottom: '1rem' }}>Customer Application Details</h3>

            {/* Application Type */}
            {customerApplicationData.ApplicationType && (
              <div className="info-card" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#e7f3ff', border: '2px solid #2A3990', borderRadius: '8px' }}>
                <h4 style={{ color: '#2A3990', fontSize: '14px', marginTop: 0, marginBottom: '0.5rem' }}>APPLICATION TYPE</h4>
                <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                  <p style={{ margin: '0.25rem 0' }}><strong>Type:</strong> {customerApplicationData.ApplicationType}</p>
                  {customerApplicationData.CustomerType && <p style={{ margin: '0.25rem 0' }}><strong>Customer Type:</strong> {customerApplicationData.CustomerType}</p>}
                  {customerApplicationData.SubmittedAt && <p style={{ margin: '0.25rem 0' }}><strong>Submitted:</strong> {new Date(customerApplicationData.SubmittedAt).toLocaleDateString()}</p>}
                  {customerApplicationData.SubmittedBy && <p style={{ margin: '0.25rem 0' }}><strong>Submitted By:</strong> {customerApplicationData.SubmittedBy}</p>}
                </div>
              </div>
            )}

            {/* Commercial Info */}
            {customerApplicationData.CustomerInfo?.commercial && (
              <div className="info-card" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#d4edda', border: '2px solid #28a745', borderRadius: '8px' }}>
                <h4 style={{ color: '#28a745', fontSize: '14px', marginTop: 0, marginBottom: '0.5rem' }}>COMMERCIAL INFORMATION</h4>
                <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                  {customerApplicationData.CustomerInfo.commercial.companyLegalName && <p style={{ margin: '0.25rem 0' }}><strong>Legal Name:</strong> {customerApplicationData.CustomerInfo.commercial.companyLegalName}</p>}
                  {customerApplicationData.CustomerInfo.commercial.dbaName && <p style={{ margin: '0.25rem 0' }}><strong>DBA:</strong> {customerApplicationData.CustomerInfo.commercial.dbaName}</p>}
                  {customerApplicationData.CustomerInfo.commercial.yearsInBusiness && <p style={{ margin: '0.25rem 0' }}><strong>Years in Business:</strong> {customerApplicationData.CustomerInfo.commercial.yearsInBusiness}</p>}
                  {customerApplicationData.CustomerInfo.commercial.fedTaxId && <p style={{ margin: '0.25rem 0' }}><strong>Federal Tax ID:</strong> {customerApplicationData.CustomerInfo.commercial.fedTaxId}</p>}
                  {customerApplicationData.CustomerInfo.commercial.mainPhone && <p style={{ margin: '0.25rem 0' }}><strong>Main Phone:</strong> {customerApplicationData.CustomerInfo.commercial.mainPhone}</p>}
                  {customerApplicationData.CustomerInfo.commercial.website && <p style={{ margin: '0.25rem 0' }}><strong>Website:</strong> {customerApplicationData.CustomerInfo.commercial.website}</p>}
                  {customerApplicationData.CustomerInfo.commercial.principleOfficer && <p style={{ margin: '0.25rem 0' }}><strong>Principal Officer:</strong> {customerApplicationData.CustomerInfo.commercial.principleOfficer}</p>}
                  {customerApplicationData.CustomerInfo.commercial.estMonthlyPurchases && <p style={{ margin: '0.25rem 0' }}><strong>Est. Monthly Purchases:</strong> ${Number(customerApplicationData.CustomerInfo.commercial.estMonthlyPurchases).toLocaleString()}</p>}
                  {customerApplicationData.CustomerInfo.commercial.poRequired !== undefined && <p style={{ margin: '0.25rem 0' }}><strong>PO Required:</strong> {customerApplicationData.CustomerInfo.commercial.poRequired ? 'Yes' : 'No'}</p>}
                  {customerApplicationData.CustomerInfo.commercial.taxExempt !== undefined && <p style={{ margin: '0.25rem 0' }}><strong>Tax Exempt:</strong> {customerApplicationData.CustomerInfo.commercial.taxExempt ? 'Yes' : 'No'}</p>}
                  {customerApplicationData.CustomerInfo.commercial.accountsPayableContact && <p style={{ margin: '0.25rem 0' }}><strong>AP Contact:</strong> {customerApplicationData.CustomerInfo.commercial.accountsPayableContact}</p>}
                  {customerApplicationData.CustomerInfo.commercial.apEmail && <p style={{ margin: '0.25rem 0' }}><strong>AP Email:</strong> {customerApplicationData.CustomerInfo.commercial.apEmail}</p>}
                </div>
              </div>
            )}

            {/* Personal Info (for personal accounts) */}
            {customerApplicationData.CustomerInfo?.personal && Object.keys(customerApplicationData.CustomerInfo.personal).length > 0 && (
              <div className="info-card" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#d4edda', border: '2px solid #28a745', borderRadius: '8px' }}>
                <h4 style={{ color: '#28a745', fontSize: '14px', marginTop: 0, marginBottom: '0.5rem' }}>PERSONAL INFORMATION</h4>
                <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                  {customerApplicationData.CustomerInfo.personal.firstName && <p style={{ margin: '0.25rem 0' }}><strong>First Name:</strong> {customerApplicationData.CustomerInfo.personal.firstName}</p>}
                  {customerApplicationData.CustomerInfo.personal.lastName && <p style={{ margin: '0.25rem 0' }}><strong>Last Name:</strong> {customerApplicationData.CustomerInfo.personal.lastName}</p>}
                  {customerApplicationData.CustomerInfo.personal.phone && <p style={{ margin: '0.25rem 0' }}><strong>Phone:</strong> {customerApplicationData.CustomerInfo.personal.phone}</p>}
                  {customerApplicationData.CustomerInfo.personal.ssn && <p style={{ margin: '0.25rem 0' }}><strong>SSN:</strong> {customerApplicationData.CustomerInfo.personal.ssn}</p>}
                  {customerApplicationData.CustomerInfo.personal.driversLicense && <p style={{ margin: '0.25rem 0' }}><strong>Driver's License:</strong> {customerApplicationData.CustomerInfo.personal.driversLicense}</p>}
                </div>
              </div>
            )}

            {/* Billing Address */}
            {customerApplicationData.CustomerInfo?.addressInfo && (
              <div className="info-card" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fff3cd', border: '2px solid #ffc107', borderRadius: '8px' }}>
                <h4 style={{ color: '#856404', fontSize: '14px', marginTop: 0, marginBottom: '0.5rem' }}>BILLING ADDRESS</h4>
                <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                  {customerApplicationData.CustomerInfo.addressInfo.billingAddress && <p style={{ margin: '0.25rem 0' }}><strong>Address:</strong> {customerApplicationData.CustomerInfo.addressInfo.billingAddress} {customerApplicationData.CustomerInfo.addressInfo.billingAddress2}</p>}
                  {customerApplicationData.CustomerInfo.addressInfo.billingCity && <p style={{ margin: '0.25rem 0' }}><strong>City:</strong> {customerApplicationData.CustomerInfo.addressInfo.billingCity}</p>}
                  {customerApplicationData.CustomerInfo.addressInfo.billingState && <p style={{ margin: '0.25rem 0' }}><strong>State:</strong> {customerApplicationData.CustomerInfo.addressInfo.billingState}</p>}
                  {customerApplicationData.CustomerInfo.addressInfo.billingZip && <p style={{ margin: '0.25rem 0' }}><strong>Zip:</strong> {customerApplicationData.CustomerInfo.addressInfo.billingZip}</p>}
                  {customerApplicationData.CustomerInfo.addressInfo.billingContact && <p style={{ margin: '0.25rem 0' }}><strong>Contact:</strong> {customerApplicationData.CustomerInfo.addressInfo.billingContact}</p>}
                  {customerApplicationData.CustomerInfo.addressInfo.billingPhone && <p style={{ margin: '0.25rem 0' }}><strong>Phone:</strong> {customerApplicationData.CustomerInfo.addressInfo.billingPhone}</p>}
                  {customerApplicationData.CustomerInfo.addressInfo.billingEmail && <p style={{ margin: '0.25rem 0' }}><strong>Email:</strong> {customerApplicationData.CustomerInfo.addressInfo.billingEmail}</p>}
                </div>
              </div>
            )}

            {/* Shipping Info */}
            {customerApplicationData.CustomerInfo?.shippingInfo && (
              <div className="info-card" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#d1ecf1', border: '2px solid #17a2b8', borderRadius: '8px' }}>
                <h4 style={{ color: '#0c5460', fontSize: '14px', marginTop: 0, marginBottom: '0.5rem' }}>SHIPPING ADDRESS</h4>
                <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                  {customerApplicationData.CustomerInfo.shippingInfo.shippingBusinessName && <p style={{ margin: '0.25rem 0' }}><strong>Business Name:</strong> {customerApplicationData.CustomerInfo.shippingInfo.shippingBusinessName}</p>}
                  {customerApplicationData.CustomerInfo.shippingInfo.shippingAddress &&<p style={{ margin: '0.25rem 0' }}><strong>Address:</strong> {customerApplicationData.CustomerInfo.shippingInfo.shippingAddress} {customerApplicationData.CustomerInfo.shippingInfo.shippingAddress2}</p>}
                  {customerApplicationData.CustomerInfo.shippingInfo.shippingCity && <p style={{ margin: '0.25rem 0' }}><strong>City:</strong> {customerApplicationData.CustomerInfo.shippingInfo.shippingCity}</p>}
                  {customerApplicationData.CustomerInfo.shippingInfo.shippingState && <p style={{ margin: '0.25rem 0' }}><strong>State:</strong> {customerApplicationData.CustomerInfo.shippingInfo.shippingState}</p>}
                  {customerApplicationData.CustomerInfo.shippingInfo.shippingZip && <p style={{ margin: '0.25rem 0' }}><strong>Zip:</strong> {customerApplicationData.CustomerInfo.shippingInfo.shippingZip}</p>}
                  {customerApplicationData.CustomerInfo.shippingInfo.shippingContact && <p style={{ margin: '0.25rem 0' }}><strong>Contact:</strong> {customerApplicationData.CustomerInfo.shippingInfo.shippingContact}</p>}
                  {customerApplicationData.CustomerInfo.shippingInfo.shippingPhone && <p style={{ margin: '0.25rem 0' }}><strong>Phone:</strong> {customerApplicationData.CustomerInfo.shippingInfo.shippingPhone}</p>}
                  {customerApplicationData.CustomerInfo.shippingInfo.shippingEmail && <p style={{ margin: '0.25rem 0' }}><strong>Email:</strong> {customerApplicationData.CustomerInfo.shippingInfo.shippingEmail}</p>}
                </div>
              </div>
            )}

            {/* Trade References */}
            {customerApplicationData.CustomerInfo?.tradeReferences && (customerApplicationData.CustomerInfo.tradeReferences.tradeName || customerApplicationData.CustomerInfo.tradeReferences.tradeName2) && (
              <div className="info-card" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8d7da', border: '2px solid #dc3545', borderRadius: '8px' }}>
                <h4 style={{ color: '#721c24', fontSize: '14px', marginTop: 0, marginBottom: '0.5rem' }}>TRADE REFERENCES</h4>
                <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                  {customerApplicationData.CustomerInfo.tradeReferences.tradeName && (
                    <div style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: customerApplicationData.CustomerInfo.tradeReferences.tradeName2 ? '1px solid #dc3545' : 'none' }}>
                      <p style={{ margin: '0.25rem 0' }}><strong>Name:</strong> {customerApplicationData.CustomerInfo.tradeReferences.tradeName}</p>
                      {customerApplicationData.CustomerInfo.tradeReferences.tradePhone && <p style={{ margin: '0.25rem 0' }}><strong>Phone:</strong> {customerApplicationData.CustomerInfo.tradeReferences.tradePhone}</p>}
                      {customerApplicationData.CustomerInfo.tradeReferences.tradeAccountNumber && <p style={{ margin: '0.25rem 0' }}><strong>Account #:</strong> {customerApplicationData.CustomerInfo.tradeReferences.tradeAccountNumber}</p>}
                      {customerApplicationData.CustomerInfo.tradeReferences.tradeAddress && <p style={{ margin: '0.25rem 0' }}><strong>Address:</strong> {customerApplicationData.CustomerInfo.tradeReferences.tradeAddress}</p>}
                      {customerApplicationData.CustomerInfo.tradeReferences.tradeCity && <p style={{ margin: '0.25rem 0' }}><strong>City/State/Zip:</strong> {customerApplicationData.CustomerInfo.tradeReferences.tradeCity}, {customerApplicationData.CustomerInfo.tradeReferences.tradeState} {customerApplicationData.CustomerInfo.tradeReferences.tradeZip}</p>}
                    </div>
                  )}
                  {customerApplicationData.CustomerInfo.tradeReferences.tradeName2 && (
                    <div>
                      <p style={{ margin: '0.25rem 0' }}><strong>Name:</strong> {customerApplicationData.CustomerInfo.tradeReferences.tradeName2}</p>
                      {customerApplicationData.CustomerInfo.tradeReferences.tradePhone2 && <p style={{ margin: '0.25rem 0' }}><strong>Phone:</strong> {customerApplicationData.CustomerInfo.tradeReferences.tradePhone2}</p>}
                      {customerApplicationData.CustomerInfo.tradeReferences.tradeAccountNumber2 && <p style={{ margin: '0.25rem 0' }}><strong>Account #:</strong> {customerApplicationData.CustomerInfo.tradeReferences.tradeAccountNumber2}</p>}
                      {customerApplicationData.CustomerInfo.tradeReferences.tradeAddress2 && <p style={{ margin: '0.25rem 0' }}><strong>Address:</strong> {customerApplicationData.CustomerInfo.tradeReferences.tradeAddress2}</p>}
                      {customerApplicationData.CustomerInfo.tradeReferences.tradeCity2 && <p style={{ margin: '0.25rem 0' }}><strong>City/State/Zip:</strong> {customerApplicationData.CustomerInfo.tradeReferences.tradeCity2}, {customerApplicationData.CustomerInfo.tradeReferences.tradeState2} {customerApplicationData.CustomerInfo.tradeReferences.tradeZip2}</p>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

      <div className='processing-form-div' style={{ flex: 1 }}>

        <section className='header-section'>
          <button className="back-button" onClick={() => navigate('/')}>⬅  Back to Dashboard</button>
          <h2 >Processing Form</h2>
        </section>

        <form onSubmit={handleSubmit} noValidate>
          <section className='applicantId-section'>
            <label htmlFor='applicantId' className='applicantId-label'>
              Applicant ID:
              <input type="text" name="applicantId" value={applicantId} onChange={(e) => setApplicantId(e.target.value.replace(/\D/g, '').slice(0, 5))} className='applicantId-input' />
            </label>
            {applicantId.length === 5 && customerName ? (
              <p className='applicantId-show'>
                Customer on File: <span className='applicantId-yes'>{customerName}</span>
              </p>
            ) : applicantId.length === 5 && !customerName && (<p className='applicantId-show'>
              Customer on File: <span className='applicantId-no'>Applicant Not Found</span>
            </p>)}
          </section>

          <div>

            <section className='processing-section-select'>
              <TerritorySelect value={processingData.territory} disabled={!isFormUnlocked} onChange={(val) => setProcessingData((prev) => ({ ...prev, territory: val }))} territoryCodes={territoryCodes} />
              <BranchSelect value={processingData.branch} disabled={!isFormUnlocked} onChange={(val) => setProcessingData((prev) => ({ ...prev, branch: val }))} branchCodes={branchCodes} />
              <TaxCodeSelect value={processingData.taxCode} disabled={!isFormUnlocked} onChange={(val) => setProcessingData((prev) => ({ ...prev, taxCode: val }))} taxCodes={taxCodes} />
              <SalespersonSelect value={processingData.salesperson} disabled={!isFormUnlocked} onChange={(val) => setProcessingData((prev) => ({ ...prev, salesperson: val }))} salesPersonOptions={salesPersonOptions} />
            </section>

            <section className='process-input-section'>
              <label htmlFor="approvedBy" style={inputLabelDisabledStyle}>
                Approved By:
                <input
                  type="text"
                  name="approvedBy"
                  disabled={!isFormUnlocked}
                  value={processingData.approvedBy.toUpperCase()}
                  onChange={handleChange("approvedBy")}
                />
              </label>

              <label htmlFor="creditLimit" style={inputLabelDisabledStyle}>
                Credit Limit:
                <input
                  name="creditLimit"
                  type="number"
                  disabled={!isFormUnlocked}
                  value={processingData.creditLimit}
                  onChange={handleChange("creditLimit")}
                />
              </label>

              <label htmlFor="billTo" style={inputLabelDisabledStyle}>
                Bill To:
                <input
                  name="billTo"
                  type="text"
                  disabled={!isFormUnlocked}
                  value={processingData.billTo.toUpperCase()}
                  onChange={handleChange("billTo")}
                />
              </label>

              <label htmlFor="customerCode" style={inputLabelDisabledStyle}>
                New Customer Code: {status}
                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                  <input
                    name="customerCode"
                    type="text"
                    disabled={!isFormUnlocked}
                    value={processingData.customerCode.toUpperCase()}
                    onChange={handleChange("customerCode")}
                    style={{
                      flex: 1,
                      border: "1px solid #ccc",
                      borderRight: "none",          // remove right border so it connects
                      borderRadius: "4px 0 0 4px", // round only left corners
                      padding: "6px 8px"
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleCodeCheck}
                    disabled={!isFormUnlocked || !processingData.customerCode || isLoading}
                    style={{
                      border: "1.5px solid #ccc",
                      borderRadius: "0 4px 4px 0",  // round only right corners
                      background: "#1582caff",
                      color: "#ffffffff",
                      padding: "6px 12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "60px",
                      cursor: "pointer",
                      marginTop: "-17px"
                    }}
                  >
                    {isCheckLoading ? <div className="spinner" /> : "Check"}
                  </button>
                </div>
              </label>

              {[
                { key: "timsCustomerName", label: "TIMS Customer Name" },
                { key: "timsShipToName", label: "TIMS Ship-To Name" },
              ].map(({ key, label }) => {
                // Not capped with maxLength so older, too-long names still show in full and can be shortened
                const len = (processingData[key] || "").length;
                const tooLong = len > TIMS_NAME_MAX;
                return (
                  <label key={key} htmlFor={key} style={inputLabelDisabledStyle}>
                    {label}:
                    <input
                      id={key}
                      name={key}
                      type="text"
                      disabled={!isFormUnlocked}
                      value={processingData[key].toUpperCase()}
                      onChange={handleChange(key)}
                      aria-invalid={tooLong}
                      aria-describedby={`${key}-count`}
                      style={tooLong ? { borderColor: "#c00" } : undefined}
                    />
                    <small id={`${key}-count`} style={{ color: tooLong ? "#c00" : "#555", fontWeight: tooLong ? "bold" : "normal" }}>
                      {len}/{TIMS_NAME_MAX}{tooLong && " — too long for TIMS, please shorten"}
                    </small>
                  </label>
                );
              })}




            </section>

            <section>
              <div style={{ display: "grid", gridTemplateColumns: ".5fr 1fr .5fr", gap: "30px", alignItems: "start" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "center" }}>
                  <label htmlFor="cod" style={checkboxDisabledStyle}>
                    C.O.D.
                  </label>
                  <input id="cod" name="cod" type="checkbox" disabled={!isFormUnlocked} checked={Boolean(processingData.cod)} onChange={toggle("cod")} className={`${!isFormUnlocked ? "disabled" : ""}`} />

                </div>

                <fieldset style={fieldsetDisabledStyle}>
                  <legend style={{ fontWeight: "bold", fontSize: "16px", color: "#2A3990" }} >
                    Check If Taxable
                  </legend>

                  <div style={{ display: "flex", flexDirection: "row", gap: "55px", justifyContent: "center", textAlign: "center", padding: "10px 30px" }}>

                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "center" }}>
                      <label htmlFor="salesTax" style={checkboxDisabledStyle}>
                        Sales Tax:
                      </label>
                      <input id="salesTax" name="salesTax" type="checkbox" disabled={!isFormUnlocked} checked={Boolean(processingData.salesTax)} onChange={toggle("salesTax")} className={`${!isFormUnlocked ? "disabled" : ""}`} />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "center" }}>
                      <label htmlFor="rentTax" style={checkboxDisabledStyle}>
                        Rent Tax:
                      </label>
                      <input id="rentTax" name="rentTax" type="checkbox" disabled={!isFormUnlocked} checked={Boolean(processingData.rentTax)} onChange={toggle("rentTax")} className={`${!isFormUnlocked ? "disabled" : ""}`} />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "center" }}>
                      <label htmlFor="leaseTax" style={checkboxDisabledStyle}>
                        Lease Tax:
                      </label>
                      <input id="leaseTax" name="leaseTax" type="checkbox" disabled={!isFormUnlocked} checked={Boolean(processingData.leaseTax)} onChange={toggle("leaseTax")} className={`${!isFormUnlocked ? "disabled" : ""}`} />
                    </div>

                  </div>
                </fieldset>

                {/* need to lock in today's date, this is just for show, no selection should be able to be made */}
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "center" }}>
                  <label htmlFor="approvalDate" style={inputLabelDisabledStyle}>
                    Approval Date:
                  </label>

                  <div
                    style={{
                      width: "50%",
                      padding: "8px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      backgroundColor: "#f5f5f5",
                      color: "#555",
                    }}
                  >
                    {formatDate(new Date())}
                  </div>
                </div>
              </div>
            </section>

          </div>



          <button type="submit" className='preview-button' disabled={!isFormUnlocked} style={buttonDisabledStyle}>Preview Process</button>
        </form>
      </div>
      </div> {/* Close flex container */}



      {isReviewing && (
        <div className="modal-backdrop">
          <div className="process-modal">
            <h3>Review Your Submission</h3>
            <p><strong>{customerName}</strong></p>
            <ul className="review-list">
              <li><strong>Territory:</strong> {processingData.territory}</li>
              <li><strong>Branch:</strong> {processingData.branch}</li>
              <li><strong>Assigned Salesperson:</strong> {processingData.salesperson}</li>
              <li><strong>Approved By:</strong> {processingData.approvedBy}</li>
              <li><strong>Credit Limit:</strong> {processingData.creditLimit}</li>
              <li><strong>C.O.D.:</strong> {processingData.cod ? "Yes" : "No"}</li>
              <li><strong>Bill To:</strong> {processingData.billTo || ''}</li>
              <li><strong>Terms:</strong> {processingData.terms}</li>
              <li><strong>Customer Code:</strong> {processingData.customerCode}</li>
              <li><strong>TIMS Customer Name:</strong> {processingData.timsCustomerName.toUpperCase()}</li>
              <li><strong>TIMS Ship-To Name:</strong> {processingData.timsShipToName.toUpperCase()}</li>
              <li><strong>Tax Code:</strong> {processingData.taxCode}</li>
              <li><strong>Approval Date:</strong> {formatDate(new Date())}</li>
              <li><strong>Sales Tax:</strong> {processingData.salesTax ? "Yes" : "No"}  &nbsp; <strong>Rent Tax:</strong> {processingData.rentTax ? "Yes" : "No"}  &nbsp; <strong>Lease Tax:</strong> {processingData.leaseTax ? "Yes" : "No"}</li>
            </ul>

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => setIsReviewing(false)}
              >
                Continue Editing
              </button>
              <button
                className="confirm-button"
                onClick={async (e) => {
                  e.preventDefault();
                  setIsReviewing(false);
                  setIsLoading(true);
                  await handleModalSubmit();
                }}
              >
                Confirm and Submit
              </button>


            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className='modal-backdrop'>
          <div className='process-loading-modal'>
            <ProgressBar progress={progress} text={`Processing...${progress}%`} />
            <p className="please-wait">
              Please wait while we process your request.
            </p>
            <p className="processing-hint">
              Processing may take 2–4 minutes. Please don’t refresh this page.
            </p>
            <p className="processing-status">
              {processingPhrases[statusIndex]}
            </p>
          </div>
        </div>
      )}

      {successInfo && !isLoading && (
        <div className='modal-backdrop'>
          <div className='process-loading-modal' role="alertdialog" aria-labelledby="success-title" style={{ height: 'auto', textAlign: 'center' }}>
            <h3 id="success-title" style={{ color: '#137200' }}>✅ Customer created in TIMS</h3>
            <p style={{ fontWeight: 'bold', margin: '1rem 0 0.25rem' }}>{successInfo.name}</p>
            <p style={{ margin: '0 0 1rem' }}>Customer Code: <strong>{successInfo.code}</strong></p>
            {successInfo.internalEmailSent && successInfo.customerEmailSent ? (
              <p style={{ marginBottom: '1.5rem' }}>The internal and customer emails have been sent.</p>
            ) : (
              // TIMS succeeded, but a follow-up email didn't go out — processor needs to handle it manually
              <p style={{ marginBottom: '1.5rem', color: '#b30000', fontWeight: 'bold' }}>
                ⚠️ The customer was created, but the{' '}
                {[!successInfo.internalEmailSent && 'internal', !successInfo.customerEmailSent && 'customer'].filter(Boolean).join(' and ')}{' '}
                email failed to send. Please follow up manually.
              </p>
            )}
            <button className="preview-button" autoFocus onClick={() => setSuccessInfo(null)}>
              Done
            </button>
          </div>
        </div>
      )}

      {lastError && (
        <div className='modal-backdrop'>
          <div className='process-loading-modal' style={{ height: 'auto', maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="error-section">
              <p style={{ color: "red" }}>❌ {lastError}</p>
              {timsErrors.length > 0 ? (
                <div>
                  <p style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>TIMS returned:</p>
                  <ul style={{ textAlign: 'left', margin: '0 0 1rem 0', paddingLeft: '1.2rem' }}>
                    {timsErrors.map(msg => <li key={msg}>{msg}</li>)}
                  </ul>
                  <p style={{ marginBottom: '1rem' }}>
                    No emails were sent and the application was kept. Fix the issue in the form (e.g. shorten the TIMS names), then process it again.
                  </p>
                  <button className="preview-button" onClick={() => { setTimsErrors([]); setLastError(null); setIsReviewing(false); }}>
                    Edit &amp; Resubmit
                  </button>
                </div>
              ) : retryCount < 3 ? (
                <button className="preview-button" onClick={handleRetry}>
                  Try Again
                </button>
              ) : (
                // if retry count is 3 or more, show error message and add close out 'x' button on top right of modal to return to preview screen
                <div>
                  <button className="close-button" onClick={() => { setIsReviewing(true); setLastError(null); }}>
                    ✖
                  </button>
                  <p>
                    If errors persist, please contact{" "}
                    <a href="mailto:contractor@sidneylee.com">contractor@sidneylee.com</a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {toast.message && (
        <div className={`toast ${toast.type}`}>
          {toast.message}
        </div>
      )}

    </div>
  );
};

export default CustomerProcessPage;

